<template>
    <div class="auth-card">
        <p class="auth-card__heading">Create an account or log in to complete your sign-up</p>

        <!-- Tab switcher -->
        <div class="tabs is-boxed is-fullwidth mb-4">
            <ul>
                <li :class="{ 'is-active': activeTab === 'login' }">
                    <a @click="activeTab = 'login'">Log In</a>
                </li>
                <li :class="{ 'is-active': activeTab === 'register' }">
                    <a @click="activeTab = 'register'">Create Account</a>
                </li>
            </ul>
        </div>

        <!-- Log In tab -->
        <div v-if="activeTab === 'login'">
            <div class="field">
                <p class="control has-icons-left">
                    <input v-model="loginEmail" class="input" type="email" inputmode="email" placeholder="Email" @input="clearError">
                    <span class="icon is-small is-left"><i class="fas fa-envelope"></i></span>
                </p>
            </div>
            <div class="field">
                <p class="control has-icons-left">
                    <input v-model="loginPassword" class="input" type="password" placeholder="Password" @input="clearError">
                    <span class="icon is-small is-left"><i class="fas fa-lock"></i></span>
                </p>
            </div>
            <div class="field">
                <button class="button is-primary is-fullwidth is-medium"
                    :class="{ 'is-loading': isLoading }"
                    :disabled="!loginEmail || !loginPassword || isLoading"
                    @click="login">
                    <span class="icon"><i class="fas fa-sign-in-alt"></i></span>
                    <span>Log In</span>
                </button>
            </div>
        </div>

        <!-- Create Account tab -->
        <div v-if="activeTab === 'register'">
            <!-- Email verification pending state -->
            <div v-if="verifyEmailSent">
                <div class="notification is-info is-light">
                    <p><strong>Check your inbox or <span style="color: #ff3860;">spam</span>!</strong></p>
                    <p>We sent a verification link to <strong>{{ regEmail }}</strong>. Click it then return here.</p>
                </div>
                <div class="field">
                    <button class="button is-primary is-fullwidth is-medium"
                        :class="{ 'is-loading': isVerifying }"
                        :disabled="isVerifying"
                        @click="checkVerified">
                        I've verified my email
                    </button>
                </div>
                <div class="field">
                    <button class="button is-light is-fullwidth"
                        :class="{ 'is-loading': isResending }"
                        :disabled="isResending"
                        @click="resendVerification">
                        Resend email
                    </button>
                </div>
                <p v-if="verifyError" class="has-text-danger mt-2 has-text-centered">{{ verifyError }}</p>
                <p v-if="resendMessage" class="has-text-success mt-2 has-text-centered">{{ resendMessage }}</p>
            </div>
            <!-- Registration form -->
            <div v-else>
                <div class="field">
                    <p class="control has-icons-left">
                        <input v-model="regName" class="input" type="text" placeholder="Full name" @input="clearError">
                        <span class="icon is-small is-left"><i class="fas fa-user"></i></span>
                    </p>
                </div>
                <div class="field">
                    <p class="control has-icons-left">
                        <input v-model="regEmail" class="input" type="email" inputmode="email" placeholder="Email" @input="clearError">
                        <span class="icon is-small is-left"><i class="fas fa-envelope"></i></span>
                    </p>
                </div>
                <div class="field">
                    <p class="control has-icons-left">
                        <input v-model="regPassword" class="input" type="password" placeholder="Password (min 6 characters)" @input="clearError">
                        <span class="icon is-small is-left"><i class="fas fa-lock"></i></span>
                    </p>
                </div>
                <div class="field">
                    <button class="button is-info is-fullwidth is-medium"
                        :class="{ 'is-loading': isLoading }"
                        :disabled="!regName || !regEmail || !regPassword || isLoading"
                        @click="register">
                        Create Account
                    </button>
                </div>
            </div>
        </div>

        <!-- Divider and Google button (hidden during email verification) -->
        <template v-if="!verifyEmailSent">
            <div class="auth-divider">
                <span>or</span>
            </div>

            <div class="field">
                <button class="button is-white is-fullwidth is-medium google-btn"
                    :class="{ 'is-loading': isGoogleLoading }"
                    :disabled="isGoogleLoading || isLoading"
                    @click="loginWithGoogle">
                    <span class="icon"><i class="fab fa-google"></i></span>
                    <span>Continue with Google</span>
                </button>
            </div>
        </template>

        <div v-if="error" class="notification is-danger is-light mt-3">
            <p>{{ error }}</p>
        </div>
    </div>
</template>

<script>
import { auth } from '@/firebase/firebaseInit';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';

export default {
    emits: ['authenticated'],
    data() {
        return {
            activeTab: 'login',
            loginEmail: '',
            loginPassword: '',
            regName: '',
            regEmail: '',
            regPassword: '',
            isLoading: false,
            isGoogleLoading: false,
            isVerifying: false,
            isResending: false,
            error: null,
            verifyEmailSent: false,
            verifyError: null,
            resendMessage: null
        }
    },
    methods: {
        clearError() {
            this.error = null;
        },
        async login() {
            this.isLoading = true;
            this.error = null;
            try {
                await this.$store.dispatch('user/login', {
                    email: this.loginEmail.toLowerCase(),
                    password: this.loginPassword
                });
                this.$emit('authenticated');
            } catch (err) {
                this.error = err.message || 'Login failed. Check your email and password.';
            }
            this.isLoading = false;
        },
        async register() {
            this.isLoading = true;
            this.error = null;
            if (this.regPassword.length < 6) {
                this.error = 'Password must be at least 6 characters.';
                this.isLoading = false;
                return;
            }
            try {
                const { user } = await createUserWithEmailAndPassword(auth, this.regEmail.toLowerCase(), this.regPassword);
                await this.$store.dispatch('user/createUser', {
                    name: this.regName.trim(),
                    email: this.regEmail.toLowerCase(),
                    phone: ''
                });
                this.$store.commit('user/setCurrentUserID', auth.currentUser.uid);
                await sendEmailVerification(user);
                this.verifyEmailSent = true;
            } catch (err) {
                if (err.code === 'auth/email-already-in-use') {
                    this.error = 'An account already exists with this email. Please log in instead.';
                } else {
                    this.error = err.message || 'Registration failed.';
                }
            }
            this.isLoading = false;
        },
        async checkVerified() {
            this.isVerifying = true;
            this.verifyError = null;
            try {
                await auth.currentUser.reload();
                if (auth.currentUser.emailVerified) {
                    this.$emit('authenticated');
                } else {
                    this.verifyError = 'Not verified yet — please check your inbox and try again.';
                }
            } catch (err) {
                this.verifyError = err.message || 'Failed to check verification status.';
            }
            this.isVerifying = false;
        },
        async resendVerification() {
            this.isResending = true;
            this.resendMessage = null;
            try {
                await sendEmailVerification(auth.currentUser);
                this.resendMessage = 'Verification email sent!';
            } catch (err) {
                this.resendMessage = err.message || 'Failed to resend email.';
            }
            this.isResending = false;
        },
        async loginWithGoogle() {
            this.isGoogleLoading = true;
            this.error = null;
            try {
                await this.$store.dispatch('user/loginWithGoogle');
                this.$emit('authenticated');
            } catch (err) {
                this.error = err.message || 'Google sign-in failed.';
            }
            this.isGoogleLoading = false;
        }
    }
}
</script>

<style scoped>
.auth-card {
    background: #fff;
    border-radius: 16px;
    padding: 1.5rem;
    border: 1.5px solid #e5e7eb;
    box-shadow: 0 4px 14px -8px rgba(15, 23, 42, 0.15);
}

.auth-card__heading {
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    text-align: center;
    margin-bottom: 1.25rem;
}

.auth-divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1rem 0;
    color: #9ca3af;
    font-size: 0.85rem;
}

.auth-divider::before,
.auth-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
}

.google-btn {
    border: 1.5px solid #e5e7eb;
}
</style>
